import { useState, useRef, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eraser, Pencil, Undo, Trash2, Save, FolderOpen, Loader2, Crown, Maximize, Minimize, ListOrdered, Play, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Colored chip config for saved-play tags.
const PLAY_TAGS = [
  { value: 'offense', label: 'Offense', chip: 'bg-orange-500 text-white', dot: 'bg-orange-500' },
  { value: 'defense', label: 'Defense', chip: 'bg-blue-600 text-white', dot: 'bg-blue-600' },
  { value: 'inbounds', label: 'Inbounds', chip: 'bg-purple-600 text-white', dot: 'bg-purple-600' },
  { value: 'press', label: 'Press', chip: 'bg-red-600 text-white', dot: 'bg-red-600' },
  { value: 'other', label: 'Other', chip: 'bg-gray-500 text-white', dot: 'bg-gray-500' },
];

const getTagMeta = (tag) => PLAY_TAGS.find((t) => t.value === tag) || null;

const COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Purple', value: '#A855F7' },
];

const STROKE_WIDTHS = [2, 4, 6, 8, 12];

// Court background image (replaces the previously hand-drawn court)
const COURT_IMAGE_URL = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_d23f2e81-7b34-4a9d-a685-406f35d1864f/artifacts/haxk4ak8_basketball-wooden-court-sport-game_18591-51439.jpg';
const COURT_FALLBACK_COLOR = '#6B4423'; // shown briefly while the image loads

let cachedCourtImage = null;
let courtImageLoadPromise = null;

const loadCourtImage = () => {
  if (cachedCourtImage) return Promise.resolve(cachedCourtImage);
  if (!courtImageLoadPromise) {
    courtImageLoadPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { cachedCourtImage = img; resolve(img); };
      img.onerror = reject;
      img.src = COURT_IMAGE_URL;
    });
  }
  return courtImageLoadPromise;
};

export default function WhiteboardPage({ user, onLogout }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [tool, setTool] = useState('pen'); // pen, eraser
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  // Court view: 'full' shows the whole court rotated to landscape, 'half' zooms into
  // one half (half-court line to the baseline under the goal). Kept in a ref too so
  // async image-load callbacks and the native resize listener always read the latest
  // value instead of a stale closure.
  const [courtView, setCourtViewState] = useState('full');
  const courtViewRef = useRef('full');
  const setCourtView = (view) => {
    courtViewRef.current = view;
    setCourtViewState(view);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Set canvas size to fill container
    const resizeCanvas = () => {
      const container = canvas.parentElement;
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight - 80; // Account for toolbar
      
      // Draw basketball court background (rotated to landscape, full or half view)
      renderCourtBackground(ctx, canvas.width, canvas.height);
      
      // Restore drawing if exists
      if (history.length > 0 && historyIndex >= 0) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0);
        img.src = history[historyIndex];
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Save initial state
    saveHistory();

    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  // Draws the (portrait-sourced) court image rotated 90deg so it sits in landscape
  // position on the canvas. When view === 'half', crops the source image to just
  // the bottom half (half-court line down to the baseline) before rotating.
  const drawCourtImageToCanvas = (ctx, img, width, height, view) => {
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate(Math.PI / 2);
    if (view === 'half') {
      const sx = 0;
      const sy = img.height / 2;
      const sw = img.width;
      const sh = img.height / 2;
      ctx.drawImage(img, sx, sy, sw, sh, -height / 2, -width / 2, height, width);
    } else {
      ctx.drawImage(img, 0, 0, img.width, img.height, -height / 2, -width / 2, height, width);
    }
    ctx.restore();
  };

  const renderCourtBackground = (ctx, width, height) => {
    const view = courtViewRef.current;
    if (cachedCourtImage) {
      drawCourtImageToCanvas(ctx, cachedCourtImage, width, height, view);
      return;
    }
    ctx.fillStyle = COURT_FALLBACK_COLOR;
    ctx.fillRect(0, 0, width, height);
    loadCourtImage().then((img) => {
      drawCourtImageToCanvas(ctx, img, width, height, courtViewRef.current);
      // Re-apply any existing drawing so the async image load can't erase it
      if (history.length > 0 && historyIndex >= 0) {
        const overlay = new Image();
        overlay.onload = () => ctx.drawImage(overlay, 0, 0);
        overlay.src = history[historyIndex];
      }
    }).catch(() => {});
  };

  const applyCourtView = (view) => {
    if (view === courtViewRef.current) return;
    setCourtView(view);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    renderCourtBackground(ctx, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL();
    setHistory([dataUrl]);
    setHistoryIndex(0);
    toast.success(view === 'half' ? 'Half Court view' : 'Full Court view');
  };

  const saveHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(canvas.toDataURL());
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.lineTo(x, y);
    if (tool === 'eraser') {
      // Punch a transparent hole, then redraw the court image behind it only
      // where transparent — reveals the real court image instead of a flat patch.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = strokeWidth * 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      if (cachedCourtImage) {
        ctx.globalCompositeOperation = 'destination-over';
        drawCourtImageToCanvas(ctx, cachedCourtImage, canvas.width, canvas.height, courtViewRef.current);
      }
      ctx.globalCompositeOperation = 'source-over';
    } else {
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      saveHistory();
    }
  };

  const undo = () => {
    if (historyIndex > 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const newIndex = historyIndex - 1;
      
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = history[newIndex];
      
      setHistoryIndex(newIndex);
    }
  };

  const clearBoard = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    renderCourtBackground(ctx, canvas.width, canvas.height);
    saveHistory();
    toast.success('Whiteboard cleared');
  };

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveTag, setSaveTag] = useState('offense');
  const [isSaving, setIsSaving] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [savedPlays, setSavedPlays] = useState([]);
  const [isLoadingPlays, setIsLoadingPlays] = useState(false);
  const [loadTagFilter, setLoadTagFilter] = useState('all');

  const savePlay = async () => {
    if (!saveName.trim()) {
      toast.error('Please give this play a name');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsSaving(true);
    try {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.92));
      if (!blob) throw new Error('Canvas export failed');
      const safeName = saveName.trim().replace(/[^a-z0-9-_. ]/gi, '').replace(/\s+/g, '_') || 'play';
      const formData = new FormData();
      formData.append('files', new File([blob], `${safeName}.png`, { type: 'image/png' }));
      formData.append('team_id', user.team_id);
      formData.append('category', 'whiteboard');
      formData.append('description', saveName.trim());
      formData.append('uploaded_by', user.id);
      formData.append('tag', saveTag);
      await axios.post(`${API}/photos/upload`, formData);
      toast.success(`"${saveName.trim()}" saved (${getTagMeta(saveTag)?.label})`);
      setShowSaveDialog(false);
      setSaveName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save play');
    } finally {
      setIsSaving(false);
    }
  };

  const openLoadDialog = async () => {
    setShowLoadDialog(true);
    setIsLoadingPlays(true);
    try {
      const res = await axios.get(`${API}/photos?team_id=${user.team_id}&category=whiteboard`);
      setSavedPlays(res.data || []);
    } catch (error) {
      toast.error('Failed to load saved plays');
    } finally {
      setIsLoadingPlays(false);
    }
  };

  const loadPlay = (photo) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const newHistory = [...history.slice(0, historyIndex + 1), canvas.toDataURL()];
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      toast.success(`Loaded "${photo.description || 'play'}"`);
      setShowLoadDialog(false);
    };
    img.onerror = () => toast.error('Could not load play image');
    img.src = `${BACKEND_URL}${photo.url}`;
  };

  const deletePlay = async (photoId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved play?')) return;
    try {
      await axios.delete(`${API}/photos/${photoId}`);
      setSavedPlays((prev) => prev.filter((p) => p.id !== photoId));
      toast.success('Play deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const [renamingPlayId, setRenamingPlayId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [dragIndex, setDragIndex] = useState(null);

  const startRename = (play, e) => {
    e.stopPropagation();
    setRenamingPlayId(play.id);
    setRenameValue(play.description || '');
  };

  const submitRename = async (playId, e) => {
    if (e) e.stopPropagation();
    if (!renameValue.trim()) {
      toast.error('Play name cannot be empty');
      return;
    }
    try {
      await axios.put(`${API}/photos/${playId}`, { description: renameValue.trim() });
      setSavedPlays((prev) => prev.map((p) => (p.id === playId ? { ...p, description: renameValue.trim() } : p)));
      setRenamingPlayId(null);
      toast.success('Renamed');
    } catch (error) {
      toast.error('Failed to rename');
    }
  };

  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e) => e.preventDefault();

  const toggleCover = async (play, e) => {
    e.stopPropagation();
    const nextIsCover = !play.is_cover;
    try {
      await axios.put(`${API}/photos/${play.id}`, { is_cover: nextIsCover });
      setSavedPlays((prev) => prev.map((p) => {
        if (p.id === play.id) return { ...p, is_cover: nextIsCover };
        // if we're setting a new cover, unset others in the same category
        if (nextIsCover && p.category === play.category) return { ...p, is_cover: false };
        return p;
      }));
      toast.success(nextIsCover ? 'Set as cover play' : 'Cleared cover');
    } catch (err) {
      toast.error('Failed to update cover');
    }
  };

  // ==== Full screen (Fullscreen API on the page wrapper; canvas re-sizes on the window resize it triggers) ====
  const pageRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await pageRef.current?.requestFullscreen();
    } catch (e) { toast.error('Full screen is not available in this browser'); }
  };

  // ==== Play Sequence Mode (chain plays for install-drill autoplay) ====
  const [showSequenceBuilder, setShowSequenceBuilder] = useState(false);
  const [sequenceName, setSequenceName] = useState('');
  const [sequencePlayIds, setSequencePlayIds] = useState([]);
  const [isSavingSequence, setIsSavingSequence] = useState(false);
  const [sequences, setSequences] = useState([]);
  const [showSequencePlayer, setShowSequencePlayer] = useState(false);
  const [activeSequence, setActiveSequence] = useState(null);
  const [sequenceStep, setSequenceStep] = useState(0);

  const loadSequences = async () => {
    try {
      const res = await axios.get(`${API}/photos/sequences?team_id=${user.team_id}`);
      setSequences(res.data || []);
    } catch (e) {
      // silent
    }
  };

  const openSequenceBuilder = async () => {
    setSequenceName('');
    setSequencePlayIds([]);
    if (savedPlays.length === 0) await openLoadDialog();
    setShowSequenceBuilder(true);
    loadSequences();
  };

  const toggleSequencePick = (id) => {
    setSequencePlayIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const saveSequence = async () => {
    if (!sequenceName.trim()) return toast.error('Give this sequence a name');
    if (sequencePlayIds.length < 2) return toast.error('Pick at least 2 plays');
    setIsSavingSequence(true);
    try {
      await axios.post(`${API}/photos/sequences`, {
        team_id: user.team_id,
        name: sequenceName.trim(),
        photo_ids: sequencePlayIds,
      });
      toast.success(`Sequence "${sequenceName.trim()}" saved`);
      setSequenceName('');
      setSequencePlayIds([]);
      loadSequences();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save sequence');
    } finally {
      setIsSavingSequence(false);
    }
  };

  const startSequence = (seq) => {
    setActiveSequence(seq);
    setSequenceStep(0);
    setShowSequencePlayer(true);
    setShowSequenceBuilder(false);
  };

  const advanceSequence = () => {
    if (!activeSequence) return;
    const next = sequenceStep + 1;
    if (next >= (activeSequence.photos || []).length) {
      toast.success('Sequence complete');
      setShowSequencePlayer(false);
      return;
    }
    setSequenceStep(next);
  };

  const deleteSequence = async (id) => {
    if (!window.confirm('Delete this sequence?')) return;
    try {
      await axios.delete(`${API}/photos/sequences/${id}`);
      setSequences((prev) => prev.filter((s) => s.id !== id));
      toast.success('Deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };
  const handleDrop = async (targetIdx) => {
    if (dragIndex === null || dragIndex === targetIdx) {
      setDragIndex(null);
      return;
    }
    // Reorder within the currently-visible filtered list, then persist that new order
    const filtered = savedPlays.filter((p) => loadTagFilter === 'all' || (p.tag || 'other') === loadTagFilter);
    const moved = filtered[dragIndex];
    const rest = filtered.filter((_, i) => i !== dragIndex);
    rest.splice(targetIdx, 0, moved);

    // Merge back into the full savedPlays list preserving the un-filtered items' positions
    const filteredIds = new Set(filtered.map((p) => p.id));
    const newFilteredIter = rest[Symbol.iterator]();
    const merged = savedPlays.map((p) => filteredIds.has(p.id) ? newFilteredIter.next().value : p);

    setSavedPlays(merged);
    setDragIndex(null);
    try {
      await axios.post(`${API}/photos/reorder`, { photo_ids: merged.map((p) => p.id) });
    } catch (e) {
      toast.error('Failed to save new order');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div ref={pageRef} className={`p-4 ${isFullscreen ? 'h-screen bg-background overflow-hidden' : 'h-[calc(100vh-2rem)]'}`} data-testid="whiteboard-page">
        {/* Toolbar */}
        <div className="bg-secondary rounded-lg p-3 mb-4 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white uppercase">Tool:</span>
            <Button
              variant={tool === 'pen' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTool('pen')}
              className={tool === 'pen' ? '' : 'border-orange-500 text-orange-500 bg-transparent hover:bg-orange-500 hover:text-white'}
              data-testid="pen-tool"
            >
              <Pencil className="h-4 w-4 mr-1" />
              Pen
            </Button>
            <Button
              variant={tool === 'eraser' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTool('eraser')}
              className={tool === 'eraser' ? '' : 'border-orange-500 text-orange-500 bg-transparent hover:bg-orange-500 hover:text-white'}
              data-testid="eraser-tool"
            >
              <Eraser className="h-4 w-4 mr-1" />
              Eraser
            </Button>
          </div>

          <div className="h-8 w-px bg-white/20" />

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white uppercase">Court:</span>
            <Button
              variant={courtView === 'full' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyCourtView('full')}
              className={courtView === 'full' ? '' : 'border-orange-500 text-orange-500 bg-transparent hover:bg-orange-500 hover:text-white'}
              data-testid="full-court-toggle"
            >
              Full Court
            </Button>
            <Button
              variant={courtView === 'half' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyCourtView('half')}
              className={courtView === 'half' ? '' : 'border-orange-500 text-orange-500 bg-transparent hover:bg-orange-500 hover:text-white'}
              data-testid="half-court-toggle"
            >
              Half Court
            </Button>
          </div>

          <div className="h-8 w-px bg-white/20" />

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white uppercase">Color:</span>
            <div className="flex gap-1">
              {COLORS.map((c) => (
                <button
                  key={c.value}
                  className={`w-6 h-6 rounded-full border-2 ${color === c.value ? 'border-accent scale-125' : 'border-white/30'}`}
                  style={{ backgroundColor: c.value }}
                  onClick={() => setColor(c.value)}
                  title={c.name}
                  data-testid={`color-${c.name.toLowerCase()}`}
                />
              ))}
            </div>
          </div>

          <div className="h-8 w-px bg-white/20" />

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white uppercase">Size:</span>
            <div className="flex gap-1">
              {STROKE_WIDTHS.map((w) => (
                <button
                  key={w}
                  className={`w-8 h-8 rounded flex items-center justify-center ${strokeWidth === w ? 'bg-accent' : 'bg-white/10'}`}
                  onClick={() => setStrokeWidth(w)}
                  data-testid={`stroke-${w}`}
                >
                  <div
                    className="rounded-full bg-white"
                    style={{ width: w + 2, height: w + 2 }}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="h-8 w-px bg-white/20" />

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={undo}
              disabled={historyIndex <= 0}
              className="border-orange-500 text-orange-500 bg-transparent hover:bg-orange-500 hover:text-white disabled:opacity-40"
              data-testid="undo-btn"
            >
              <Undo className="h-4 w-4 mr-1" />
              Undo
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={clearBoard}
              data-testid="clear-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clear
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowSaveDialog(true)}
              data-testid="save-play-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              Save Play
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openLoadDialog}
              className="border-orange-500 text-orange-500 bg-transparent hover:bg-orange-500 hover:text-white"
              data-testid="load-play-btn"
            >
              <FolderOpen className="h-4 w-4 mr-1" />
              Load
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openSequenceBuilder}
              className="border-orange-500 text-orange-500 bg-transparent hover:bg-orange-500 hover:text-white"
              data-testid="sequence-mode-btn"
            >
              <ListOrdered className="h-4 w-4 mr-1" />
              Sequence
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
              className="border-orange-500 text-orange-500 bg-transparent hover:bg-orange-500 hover:text-white"
              data-testid="fullscreen-btn"
            >
              {isFullscreen ? <Minimize className="h-4 w-4 mr-1" /> : <Maximize className="h-4 w-4 mr-1" />}
              {isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div className="bg-[#6B4423] rounded-lg overflow-hidden h-[calc(100%-5rem)] relative">
          <canvas
            ref={canvasRef}
            className="cursor-crosshair touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            data-testid="whiteboard-canvas"
          />
        </div>

        {/* Save Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="max-w-md" data-testid="save-play-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Save className="h-5 w-5" />
                Save Play
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Play Name</Label>
                <Input
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder='e.g. "Motion Offense", "Zone Defense", "Inbounds Play 3"'
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') savePlay(); }}
                  data-testid="save-play-name-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={saveTag} onValueChange={setSaveTag}>
                  <SelectTrigger data-testid="save-play-tag-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAY_TAGS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="inline-flex items-center gap-2">
                          <span className={`inline-block w-3 h-3 rounded-full ${t.dot}`} />
                          {t.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Saved plays live in <strong>Photos → Whiteboard</strong> and are re-loadable here (filter by category).
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
                <Button onClick={savePlay} disabled={isSaving} data-testid="submit-save-play-btn">
                  {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Load Dialog */}
        <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="load-play-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                Saved Plays
              </DialogTitle>
            </DialogHeader>
            {isLoadingPlays ? (
              <div className="py-8 text-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
              </div>
            ) : savedPlays.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground italic">
                No saved plays yet. Draw something and hit Save Play to see it here.
              </p>
            ) : (
              <>
                {/* Filter chips */}
                <div className="flex flex-wrap gap-2 pb-2 border-b" data-testid="play-tag-filters">
                  <button
                    type="button"
                    onClick={() => setLoadTagFilter('all')}
                    className={`text-xs uppercase font-semibold px-3 py-1 rounded-full border-2 transition ${
                      loadTagFilter === 'all' ? 'bg-foreground text-background border-foreground' : 'border-muted-foreground/30 text-muted-foreground hover:border-foreground'
                    }`}
                    data-testid="tag-filter-all"
                  >
                    All ({savedPlays.length})
                  </button>
                  {PLAY_TAGS.map((t) => {
                    const count = savedPlays.filter((p) => (p.tag || 'other') === t.value).length;
                    if (count === 0) return null;
                    const active = loadTagFilter === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setLoadTagFilter(t.value)}
                        className={`text-xs uppercase font-semibold px-3 py-1 rounded-full border-2 transition ${
                          active ? `${t.chip} border-transparent` : 'border-muted-foreground/30 text-muted-foreground hover:border-foreground'
                        }`}
                        data-testid={`tag-filter-${t.value}`}
                      >
                        <span className={`inline-block w-2 h-2 rounded-full mr-1 ${active ? 'bg-white' : t.dot}`} />
                        {t.label} ({count})
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                  {savedPlays
                    .filter((p) => loadTagFilter === 'all' || (p.tag || 'other') === loadTagFilter)
                    .map((play, idx) => {
                      const meta = getTagMeta(play.tag);
                      const isRenaming = renamingPlayId === play.id;
                      return (
                        <div
                          key={play.id}
                          draggable
                          onDragStart={() => handleDragStart(idx)}
                          onDragOver={handleDragOver}
                          onDrop={() => handleDrop(idx)}
                          className={`border-2 rounded-lg overflow-hidden cursor-move transition-all relative ${
                            play.is_cover
                              ? 'border-amber-400 ring-2 ring-amber-300/50 hover:border-amber-500'
                              : 'hover:border-primary hover:-translate-y-1'
                          } ${dragIndex === idx ? 'opacity-40' : ''}`}
                          onClick={() => !isRenaming && loadPlay(play)}
                          data-testid={`saved-play-${play.id}`}
                        >
                          {play.is_cover && (
                            <span
                              className="absolute top-2 right-2 bg-amber-400 text-black text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shadow flex items-center gap-1 z-10"
                              data-testid={`play-cover-badge-${play.id}`}
                              title="Cover play (hero image for printable playbook)"
                            >
                              <Crown className="h-3 w-3" />
                              Cover
                            </span>
                          )}
                          {meta && (
                            <span
                              className={`absolute top-2 left-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.chip} shadow`}
                              data-testid={`play-tag-${play.id}`}
                            >
                              {meta.label}
                            </span>
                          )}
                          <img src={`${BACKEND_URL}${play.url}`} alt={play.description} className="w-full h-32 object-cover bg-black" draggable={false} />
                          <div className="p-2 flex items-center justify-between gap-2">
                            {isRenaming ? (
                              <Input
                                autoFocus
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') submitRename(play.id, e);
                                  if (e.key === 'Escape') { setRenamingPlayId(null); }
                                }}
                                onBlur={(e) => submitRename(play.id, e)}
                                className="h-7 text-sm"
                                data-testid={`rename-play-input-${play.id}`}
                              />
                            ) : (
                              <>
                                <span
                                  className="text-sm font-semibold truncate flex-1"
                                  title={play.description}
                                  onDoubleClick={(e) => startRename(play, e)}
                                >
                                  {play.description || 'Untitled Play'}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-7 w-7 shrink-0 ${play.is_cover ? 'text-amber-500 hover:text-amber-600' : 'text-muted-foreground hover:text-amber-500'}`}
                                  onClick={(e) => toggleCover(play, e)}
                                  data-testid={`cover-play-btn-${play.id}`}
                                  title={play.is_cover ? 'Remove as cover' : 'Set as cover'}
                                >
                                  <Crown className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 shrink-0"
                                  onClick={(e) => startRename(play, e)}
                                  data-testid={`rename-play-btn-${play.id}`}
                                  title="Rename"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-500 hover:text-red-700 shrink-0"
                                  onClick={(e) => deletePlay(play.id, e)}
                                  data-testid={`delete-play-${play.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Sequence Builder Dialog */}
        <Dialog open={showSequenceBuilder} onOpenChange={setShowSequenceBuilder}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sequence-builder-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <ListOrdered className="h-5 w-5" />
                Play Sequence
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Chain 2-12 saved plays into a numbered sequence. Great for install drills — the sequence player advances one play at a time on tap.
              </p>

              {/* Existing sequences */}
              {sequences.length > 0 && (
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Existing Sequences</Label>
                  <div className="space-y-2 mt-1">
                    {sequences.map((s) => (
                      <div key={s.id} className="flex items-center justify-between border rounded p-2" data-testid={`sequence-row-${s.id}`}>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{s.name}</p>
                          <p className="text-xs text-muted-foreground">{(s.photos || []).length} plays</p>
                        </div>
                        <Button size="sm" onClick={() => startSequence(s)} data-testid={`play-sequence-${s.id}`}>
                          <Play className="h-3.5 w-3.5 mr-1" /> Play
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteSequence(s.id)} className="ml-1 text-red-500">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New sequence builder */}
              <div className="border-t pt-4">
                <Label className="text-xs uppercase text-muted-foreground">Build New Sequence</Label>
                <Input
                  value={sequenceName}
                  onChange={(e) => setSequenceName(e.target.value)}
                  placeholder="e.g. Motion Install — Day 1"
                  className="mt-2"
                  data-testid="sequence-name-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {sequencePlayIds.length} play(s) selected. Click plays below to add/remove; order is the order you click.
                </p>
                {savedPlays.length === 0 ? (
                  <p className="text-sm italic text-muted-foreground py-4">Save at least 2 plays first.</p>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                    {savedPlays.map((play) => {
                      const idx = sequencePlayIds.indexOf(play.id);
                      const picked = idx !== -1;
                      return (
                        <div
                          key={play.id}
                          onClick={() => toggleSequencePick(play.id)}
                          className={`relative border-2 rounded overflow-hidden cursor-pointer transition ${picked ? 'border-orange-500 ring-2 ring-orange-300' : 'border-muted hover:border-primary'}`}
                          data-testid={`sequence-pick-${play.id}`}
                        >
                          {picked && (
                            <span className="absolute top-1 left-1 bg-orange-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
                              {idx + 1}
                            </span>
                          )}
                          <img src={`${BACKEND_URL}${play.url}`} alt={play.description} className="w-full h-20 object-cover bg-black" />
                          <p className="text-[11px] p-1 truncate">{play.description || 'Untitled'}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex justify-end mt-3">
                  <Button onClick={saveSequence} disabled={isSavingSequence || sequencePlayIds.length < 2} data-testid="save-sequence-btn">
                    {isSavingSequence ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Sequence
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Sequence Player Dialog (autoplay through chained plays) */}
        <Dialog open={showSequencePlayer} onOpenChange={setShowSequencePlayer}>
          <DialogContent className="max-w-4xl" data-testid="sequence-player-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl">
                {activeSequence?.name || 'Sequence'} — Step {sequenceStep + 1} of {activeSequence?.photos?.length || 0}
              </DialogTitle>
            </DialogHeader>
            {activeSequence && activeSequence.photos?.[sequenceStep] && (
              <div className="space-y-4">
                <div className="aspect-video bg-black rounded overflow-hidden">
                  <img
                    src={`${BACKEND_URL}${activeSequence.photos[sequenceStep].url}`}
                    alt={activeSequence.photos[sequenceStep].description}
                    className="w-full h-full object-contain"
                    data-testid="sequence-current-image"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="font-semibold">
                    {activeSequence.photos[sequenceStep].description || 'Untitled Play'}
                  </p>
                  <Button onClick={advanceSequence} data-testid="sequence-next-btn">
                    {sequenceStep + 1 >= activeSequence.photos.length ? 'Finish' : (<>Next Play<ChevronRight className="h-4 w-4 ml-1" /></>)}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
